import { LitElement, html, css, nothing } from "lit";

/**
 * Full-page level-select navigation with a "pick a track" jukebox model.
 *
 * On mobile: one card visible at a time with [←][Enter][→] controls below.
 * On wider screens (≥900px): the prev and next cards are visible on either
 * side of the active card, scaled and darkened to de-emphasise them while
 * hinting that more choices exist. Side cards are decorative — not clickable.
 *
 * Pressing a nav arrow briefly depresses the active card before the slide
 * begins, giving tactile feedback that the action registered. The Enter
 * button is disabled while cards are loading or mid-transition.
 *
 * @element level-select
 * @prop {Array}   cards   - Array of {title, summary, link} objects (JSON attr)
 * @prop {Boolean} overlay - When true, renders as fixed overlay with close button
 * @fires close-overlay    - Dispatched when overlay dismiss is requested
 */
export class LevelSelectElement extends LitElement {
  static styles = css`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      outline: none;
      position: relative;
      overflow: hidden;
    }

    :host([overlay]) {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: var(--level-select-bg);
      color: var(--level-select-color);
      transform: translateY(-100%);
      transition: transform 0.4s ease-in-out;
      pointer-events: none;
    }

    :host([overlay][open]) {
      transform: translateY(0);
      pointer-events: auto;
    }

    /* ── Close button (overlay only) ── */
    .close-btn {
      position: absolute;
      top: var(--space-lg);
      right: var(--space-lg);
      z-index: 1;
      background: none;
      border: 2px solid currentColor;
      border-radius: 0.25rem;
      color: inherit;
      font-size: var(--step-1);
      padding: var(--space-xs) var(--space-md);
      cursor: pointer;
      line-height: 1;
    }

    .close-btn:hover,
    .close-btn:focus-visible {
      background: var(--level-select-color);
      color: var(--level-select-bg);
    }

    /* ── CRT overlay: scanlines + vignette above all content ── */
    .crt-overlay {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 100;
      background:
        repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0, 0, 0, 0.05) 2px,
          rgba(0, 0, 0, 0.05) 4px
        ),
        radial-gradient(
          ellipse at 50% 40%,
          transparent 60%,
          rgba(29, 23, 60, 0.35) 100%
        );
    }

    /* ── Header: title + hint above the card deck ── */
    .nav-header {
      text-align: center;
      padding: var(--space-sm) 0;
      flex-shrink: 0;
    }

    .nav-header__title {
      font-family: var(--font-heading);
      font-size: var(--step-2);
      letter-spacing: 0.15em;
      text-transform: uppercase;
      margin: 0 0 var(--space-xs);
      text-shadow:
        -2px 0 rgba(236, 70, 70, 0.35),
        2px 0 rgba(141, 211, 248, 0.35);
    }

    .nav-header__hint {
      font-family: var(--font-body);
      font-size: var(--step--1);
      letter-spacing: 0.1em;
      text-transform: uppercase;
      opacity: 0.6;
      margin: 0;
    }

    /* ── Stage: column container for deck + controls ── */
    .stage {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
      width: 100%;
      max-width: 40rem;
      height: 100%;
      max-height: min(90dvh, 36rem);
      padding: 0 var(--space-sm);
      box-sizing: border-box;
    }

    /* ── Controls row: [←][Enter][→] ── */
    .controls {
      display: flex;
      gap: var(--space-xs);
      padding: 0 var(--space-sm);
    }

    /*
     * Shared base for all three control buttons (nav arrows + Enter).
     * flex: 1 gives each button an equal base; .enter-btn overrides to flex: 3
     * to make it the dominant center piece.
     */
    .ctrl-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      min-height: 2.75rem;
      padding: var(--space-sm) var(--space-md);
      background: var(--level-select-card-bg);
      border: 2px solid currentColor;
      border-radius: 0.25rem;
      color: var(--level-select-color);
      font-size: var(--step-1);
      line-height: 1;
      text-decoration: none;
      cursor: pointer;
      position: relative;
      z-index: 1;
      /* Pixel-art raised shadow; offset matches :active translate */
      box-shadow: 4px 4px 0 var(--level-select-color);
      transition:
        background-color 0.15s,
        color 0.15s,
        transform 0.08s,
        box-shadow 0.08s;
    }

    /* Inversion on hover/focus — shadow dropped (inversion is the signal) */
    .ctrl-btn:not([aria-disabled="true"]):hover,
    .ctrl-btn:not([aria-disabled="true"]):focus-visible {
      background: var(--level-select-color);
      color: var(--level-select-bg);
      box-shadow: none;
    }

    /* Translate by the shadow offset so the button appears to press down */
    .ctrl-btn:not([aria-disabled="true"]):active {
      transform: translate(4px, 4px);
      box-shadow: none;
    }

    /* Enter: wider center piece */
    .enter-btn {
      flex: 3;
    }

    /* Disabled Enter: flat and dimmed */
    .enter-btn[aria-disabled="true"] {
      opacity: 0.35;
      cursor: not-allowed;
      box-shadow: none;
    }

    @media (min-width: 900px) {
      .ctrl-btn {
        font-size: var(--step-0);
      }

      .stage {
        padding: 0 var(--space-lg);
      }
    }

    /* ------------------------------------------------------------------
       Deck: the card container.
       No overflow constraint when idle — the floating shadow renders freely.
       overflow:hidden is added only during slide transitions to clip the
       cards in motion to the deck bounds.
    ------------------------------------------------------------------ */
    .deck {
      position: relative;
      flex: 1 1 auto;
      /* min-height: 0 lets this shrink in a column flex context */
      min-height: 0;
    }

    .deck--transitioning {
      overflow: hidden;
    }

    /* ------------------------------------------------------------------
       Card: base (hidden)
    ------------------------------------------------------------------ */
    .card {
      position: absolute;
      inset: 0;
      padding: var(--space-sm) var(--space-sm);
      box-sizing: border-box;
      opacity: 0;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* ------------------------------------------------------------------
       Card: active
    ------------------------------------------------------------------ */
    .card--active {
      opacity: 1;
      pointer-events: auto;
      z-index: 1;
    }

    /* ------------------------------------------------------------------
       Card: desktop carousel
       All cards participate in position transitions so the entire set
       revolves smoothly when _activeIndex changes. CSS animations (slide
       keyframes, float) always win over transitions for the same property,
       so there is no conflict with the mobile slide behaviour.
    ------------------------------------------------------------------ */
    @media (min-width: 900px) {
      .card {
        transition: transform 0.5s ease, filter 0.5s ease, opacity 0.4s ease;
      }

      .card--prev,
      .card--next {
        opacity: 1;
        /* pointer-events stays none from base .card */
      }

      .card--prev {
        transform: translateX(-70%) scale(0.8);
        filter: brightness(0.5);
      }

      .card--next {
        transform: translateX(70%) scale(0.8);
        filter: brightness(0.5);
      }
    }

    /* ------------------------------------------------------------------
       Card inner: the visual surface — block flow so float works
    ------------------------------------------------------------------ */
    .card-inner {
      display: block;
      width: 100%;
      box-sizing: border-box;
      padding: var(--space-lg);
      overflow: hidden;
      background: var(--level-select-card-bg);
      border: 2px solid currentColor;
      outline: 2px solid currentColor;
      outline-offset: -7px;
      border-radius: 0.5rem;
      color: var(--level-select-color);
    }

    /* ── Thumbnail: floated left, text wraps around it ── */
    .card__img {
      float: left;
      width: 42%;
      aspect-ratio: 1;
      display: block;
      object-fit: cover;
      margin: 0 var(--space-md) var(--space-sm) 0;
    }

    .card__img--placeholder {
      background: linear-gradient(
        135deg,
        color-mix(in oklch, var(--level-select-color) 10%, var(--level-select-bg)),
        color-mix(in oklch, var(--level-select-color) 20%, var(--level-select-bg))
      );
    }

    /*
     * Float animation on the active card only.
     *
     * box-shadow is animated alongside the Y translation: as the card rises
     * the shadow deepens and spreads; as it settles the shadow tightens.
     * This sells the illusion that the card is casting light onto the
     * surface below it.
     *
     * perspective() is inlined inside transform rather than set as a CSS
     * property on a parent — this is the only form that reliably works
     * inside a Shadow DOM at all browser/Lit versions.
     */
    .card--active .card-inner {
      animation: card-float 4s ease-in-out infinite;
    }

    @keyframes card-float {
      0% {
        transform: perspective(800px) rotateY(-3deg) translateY(0px);
        box-shadow:
          0 10px 24px rgba(0, 0, 0, 0.55),
          0 4px 8px rgba(0, 0, 0, 0.3);
      }
      50% {
        transform: perspective(800px) rotateY(3deg) translateY(-10px);
        box-shadow:
          0 24px 56px rgba(0, 0, 0, 0.75),
          0 10px 20px rgba(0, 0, 0, 0.5);
      }
      100% {
        transform: perspective(800px) rotateY(-3deg) translateY(0px);
        box-shadow:
          0 10px 24px rgba(0, 0, 0, 0.55),
          0 4px 8px rgba(0, 0, 0, 0.3);
      }
    }

    /*
     * Press animation: more specific than .card--active .card-inner so it
     * overrides card-float when both classes are present.
     */
    @keyframes card-press {
      from { transform: scale(1) translateY(0); }
      to   { transform: scale(0.96) translateY(6px); }
    }

    .card--active.card--pressed .card-inner {
      animation: card-press 0.15s ease-in forwards;
    }

    /* ------------------------------------------------------------------
       Slide transition keyframes
    ------------------------------------------------------------------ */
    @keyframes slide-out-next {
      from { transform: translateX(0);     opacity: 1;   }
      to   { transform: translateX(-110%); opacity: 0.4; }
    }

    @keyframes slide-out-prev {
      from { transform: translateX(0);    opacity: 1;   }
      to   { transform: translateX(110%); opacity: 0.4; }
    }

    @keyframes slide-in-next {
      from { transform: translateX(110%);  opacity: 0.4; }
      to   { transform: translateX(0);     opacity: 1;   }
    }

    @keyframes slide-in-prev {
      from { transform: translateX(-110%); opacity: 0.4; }
      to   { transform: translateX(0);     opacity: 1;   }
    }

    .card--exit-next {
      opacity: 1;
      pointer-events: none;
      animation: slide-out-next 0.4s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }

    .card--exit-prev {
      opacity: 1;
      pointer-events: none;
      animation: slide-out-prev 0.4s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }

    .card--enter-next {
      opacity: 1;
      pointer-events: none;
      animation: slide-in-next 0.4s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }

    .card--enter-prev {
      opacity: 1;
      pointer-events: none;
      animation: slide-in-prev 0.4s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }

    /*
     * Honour reduced-motion preferences.
     * Slide and press animations are shortened to near-zero so animationend
     * still fires and the transition state machine completes correctly.
     * Float is suppressed entirely and replaced with a static shadow.
     */
    @media (prefers-reduced-motion: reduce) {
      .card--exit-next,
      .card--exit-prev,
      .card--enter-next,
      .card--enter-prev,
      .card--active.card--pressed .card-inner {
        animation-duration: 0.01ms;
      }

      .card--active:not(.card--pressed) .card-inner {
        animation: none;
        box-shadow:
          0 16px 40px rgba(0, 0, 0, 0.65),
          0 6px 12px rgba(0, 0, 0, 0.4);
      }
    }

    /* ------------------------------------------------------------------
       Card content
    ------------------------------------------------------------------ */
    h2 {
      margin: 0 0 var(--space-xs);
      font-family: var(--font-heading);
      font-size: var(--step-2);
      font-weight: 700;
      line-height: 1.15;
      text-shadow:
        -1px 0 rgba(236, 70, 70, 0.35),
        1px 0 rgba(141, 211, 248, 0.35);
    }

    p {
      margin: 0;
      font-family: var(--font-body);
      font-size: var(--step-0);
      line-height: 1.6;
      opacity: 0.8;
    }

    @media (min-width: 900px) {
      h2 { font-size: var(--step-1); }
      p  { font-size: var(--step--1); }
    }
  `;

  static properties = {
    cards:                { type: Array },
    overlay:              { type: Boolean, reflect: true },
    _activeIndex:         { state: true },
    _pressedIndex:        { state: true },
    _carouselAnimating:   { state: true },
    _transitioning:       { state: true },
    _outgoingIndex:       { state: true },
    _incomingIndex:       { state: true },
    _transitionDirection: { state: true },
  };

  constructor() {
    super();
    this.cards = [];
    this.overlay = false;
    this._activeIndex = 0;
    this._pressedIndex = null;
    this._carouselAnimating = false;
    this._transitioning = false;
    this._outgoingIndex = null;
    this._incomingIndex = null;
    this._transitionDirection = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("tabindex", "0");
    this._boundKeydown = this._onKeydown.bind(this);
    this.addEventListener("keydown", this._boundKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this._boundKeydown);
  }

  updated(changedProperties) {
    if (changedProperties.has("overlay")) {
      if (this.overlay) {
        this.setAttribute("role", "dialog");
        this.setAttribute("aria-modal", "true");
        this.setAttribute("aria-label", "Site navigation");
      } else {
        this.removeAttribute("role");
        this.removeAttribute("aria-modal");
        this.removeAttribute("aria-label");
      }
    }
  }

  /** True whenever the Enter button and nav arrows should reject input. */
  get _isDisabled() {
    return (
      this.cards.length === 0 ||
      this._transitioning ||
      this._carouselAnimating ||
      this._pressedIndex !== null
    );
  }

  /** True when the viewport is wide enough for the three-card carousel. */
  _isCarousel() {
    return window.matchMedia("(min-width: 900px)").matches;
  }

  /**
   * Return the CSS modifier class(es) for the card at index i.
   * @param {number} i
   * @returns {string}
   */
  _cardClass(i) {
    if (this._transitioning) {
      if (i === this._outgoingIndex) return `card--exit-${this._transitionDirection}`;
      if (i === this._incomingIndex) return `card--enter-${this._transitionDirection}`;
      return "";
    }
    if (i === this._activeIndex) {
      return this._pressedIndex !== null ? "card--active card--pressed" : "card--active";
    }
    const len = this.cards.length;
    if (i === (this._activeIndex - 1 + len) % len) return "card--prev";
    if (i === (this._activeIndex + 1) % len)        return "card--next";
    return "";
  }

  render() {
    const disabled   = this._isDisabled;
    const activeLink = this.cards[this._activeIndex]?.link;

    return html`
      ${this.overlay
        ? html`
            <div class="crt-overlay" aria-hidden="true"></div>
            <button
              class="close-btn"
              @click="${this._dismiss}"
              aria-label="Close navigation"
            >&times;</button>`
        : nothing}

      <div class="stage">
        <div class="nav-header">
          <p class="nav-header__title">Select Stage</p>
          <p class="nav-header__hint">&#8592; browse &#8594; &middot; enter to visit</p>
        </div>
        <div class="deck ${this._transitioning ? "deck--transitioning" : ""}">
          ${this.cards.map((card, i) => html`
            <div
              class="card ${this._cardClass(i)}"
              @animationend="${i === this._incomingIndex ? () => this._onEnterEnd() : nothing}"
            >
              <div class="card-inner">
                ${card.thumbnail
                  ? html`<img class="card__img" src="${card.thumbnail}" alt="" aria-hidden="true">`
                  : html`<div class="card__img card__img--placeholder" aria-hidden="true"></div>`
                }
                <h2>${card.title}</h2>
                <p>${card.blurb ?? card.summary}</p>
              </div>
            </div>
          `)}
        </div>

        <div class="controls">
          <button class="ctrl-btn" @click="${this._prev}" aria-label="Previous">&larr;</button>
          <a
            class="ctrl-btn enter-btn"
            href="${!disabled && activeLink ? activeLink : nothing}"
            aria-disabled="${disabled ? "true" : nothing}"
            tabindex="${disabled ? "-1" : nothing}"
          >Enter</a>
          <button class="ctrl-btn" @click="${this._next}" aria-label="Next">&rarr;</button>
        </div>
      </div>
    `;
  }

  /**
   * Press the active card, then navigate — using a CSS transition carousel
   * on wide viewports or the slide keyframe animation on mobile.
   *
   * The press animation always completes (or times out) before the nav
   * begins, giving tactile confirmation that the input registered.
   *
   * @param {'next'|'prev'} direction
   */
  async _triggerNav(direction) {
    if (this._isDisabled || this.cards.length < 2) return;

    // 1. Press the active card down.
    this._pressedIndex = this._activeIndex;
    await this.updateComplete;

    const inner = this.shadowRoot.querySelector(".card--active.card--pressed .card-inner");
    await new Promise((resolve) => {
      if (!inner) { resolve(); return; }
      inner.addEventListener("animationend", resolve, { once: true });
      setTimeout(resolve, 300); // Fallback if animationend does not fire
    });

    if (!this.isConnected) return;

    const fromIndex = this._pressedIndex;
    this._pressedIndex = null;

    const len = this.cards.length;
    const targetIndex =
      direction === "next"
        ? (fromIndex + 1) % len
        : (fromIndex - 1 + len) % len;

    if (this._isCarousel()) {
      // 2a. Desktop: change active index directly. All cards reposition via
      //     CSS transitions simultaneously, producing the carousel revolve.
      //     _carouselAnimating disables controls for the transition duration.
      this._carouselAnimating = true;
      this._activeIndex = targetIndex;
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (this.isConnected) this._carouselAnimating = false;
    } else {
      // 2b. Mobile: directional slide keyframe animation.
      this._beginTransition(targetIndex, direction);
    }
  }

  _next() { this._triggerNav("next"); }
  _prev() { this._triggerNav("prev"); }

  /**
   * Start a directional card transition.
   * @param {number} targetIndex
   * @param {'next'|'prev'} direction
   */
  _beginTransition(targetIndex, direction) {
    this._outgoingIndex = this._activeIndex;
    this._incomingIndex = targetIndex;
    this._transitionDirection = direction;
    this._transitioning = true;
  }

  /**
   * Called when the entering card's slide-in animation completes.
   * Promotes the incoming card to active and clears all transition state.
   */
  _onEnterEnd() {
    this._activeIndex = this._incomingIndex;
    this._outgoingIndex = null;
    this._incomingIndex = null;
    this._transitionDirection = null;
    this._transitioning = false;
  }

  /** @param {KeyboardEvent} e */
  _onKeydown(e) {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        this._next();
        break;
      case "ArrowLeft":
        e.preventDefault();
        this._prev();
        break;
      case "Escape":
        if (this.overlay) {
          e.preventDefault();
          this._dismiss();
        }
        break;
      case "Tab":
        if (!this.overlay) break;
        this._trapFocus(e);
        break;
    }
  }

  /**
   * Trap Tab/Shift+Tab focus within the shadow DOM when running as an overlay.
   * Queries all naturally focusable elements and cycles through them.
   * @param {KeyboardEvent} e
   */
  _trapFocus(e) {
    const focusable = Array.from(
      this.shadowRoot.querySelectorAll('button:not([disabled]), a[href]')
    ).filter((el) => el.getAttribute("aria-disabled") !== "true");

    if (focusable.length === 0) return;

    e.preventDefault();

    const active = this.shadowRoot.activeElement;
    const currentIndex = focusable.indexOf(active);

    if (e.shiftKey) {
      const prev = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
      focusable[prev].focus();
    } else {
      const next = currentIndex < 0 || currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
      focusable[next].focus();
    }
  }

  /**
   * Jump to the card that best represents the given path, without animation.
   *
   * Resolution order:
   *   1. Exact path match against card.link
   *   2. data-section attribute on <body> (set by each layout's front matter)
   *   3. Longest prefix match against card.link
   *   4. Fall back to index 0
   *
   * The data-section fallback exists because several Bridgetown collections
   * use slugs that don't share a path prefix with their nav section
   * (e.g. /library_anime/* belongs to the /library card).
   *
   * @param {string} path - URL pathname to match against card links
   */
  showCardForPath(path) {
    const normalized = path.replace(/\/+$/, "") || "/";

    // 1. Exact match
    let index = this.cards.findIndex(
      (card) => (card.link.replace(/\/+$/, "") || "/") === normalized,
    );

    // 2. Section declared by the layout via data-section on <body>
    if (index < 0) {
      const section = document.body?.dataset?.section;
      if (section) {
        index = this.cards.findIndex(
          (card) => (card.link.replace(/\/+$/, "") || "/") === `/${section}`,
        );
      }
    }

    // 3. Longest prefix match (avoids false positives from short prefixes)
    if (index < 0) {
      let bestLen = -1;
      this.cards.forEach((card, i) => {
        const cardPath = card.link.replace(/\/+$/, "") || "/";
        if (
          cardPath !== "/" &&
          (normalized === cardPath || normalized.startsWith(cardPath + "/")) &&
          cardPath.length > bestLen
        ) {
          bestLen = cardPath.length;
          index = i;
        }
      });
    }

    this._activeIndex = index >= 0 ? index : 0;
    this._pressedIndex = null;
    this._carouselAnimating = false;
    this._transitioning = false;
    this._outgoingIndex = null;
    this._incomingIndex = null;
    this._transitionDirection = null;
  }

  _dismiss() {
    this.dispatchEvent(
      new CustomEvent("close-overlay", { bubbles: true, composed: true }),
    );
  }
}

customElements.define("level-select", LevelSelectElement);
